import { Box, Heading, Loading, Message, Select } from 'ui'
import { useCompanyMembers } from 'queries/company'
import styled from 'styled-components'
import { useSupervisorOptions } from './useSupervisorOptions'
import { Selectable } from 'typings/selectable_ext'
import { pick } from 'ramda'
import { SupervisorCard } from './SupervisorCard'
import {
  ShiftSupervisor,
  StaffForm,
  SupervisorRequestType,
} from '../../schemas'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { useShiftEditor } from 'pages/ShiftEditorPage/useShiftEditor'

const Container = styled(Box)`
  ${({ theme: { colors } }) => `
    .other-team-member {
      padding: 1rem;
      > div {
        background-color: ${colors.primaries[50]};
        color: ${colors.primary};
      }
      
      &:hover {
        > div {
          background-color: ${colors.primary};
          color: ${colors.white};
        }
      }
    }
  `}
`

export function Supervisors() {
  const { data, isLoading } = useCompanyMembers()
  const { options, CustomOption, customFilterOption } =
    useSupervisorOptions(data)

  const {
    control,
    register,
    trigger,
    formState: { errors },
  } = useFormContext<StaffForm>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'supervisors',
  })

  const addSupervisor = (supervisor: Parameters<typeof append>[0]) => {
    append(supervisor)
    // RHF 8 alpha only writes field-array resolver errors; it never unsets
    // them after append/remove, so the min(1) message would stick.
    void trigger('supervisors')
  }

  const removeSupervisor = (index: number) => {
    remove(index)
    void trigger('supervisors')
  }

  const { isOnDemandDeliveryBlock } = useShiftEditor()

  if (isLoading) {
    return <Loading />
  }

  const heading = isOnDemandDeliveryBlock ? 'Onsite Contact' : 'Supervisors'
  const supervisorsError =
    errors.supervisors?.root?.message || errors.supervisors?.message

  return (
    <Container mt={4}>
      <Heading level={3}>{heading}</Heading>
      <Select
        aria-label={'Select Supervisor'}
        components={{ Option: CustomOption }}
        value={null}
        options={options}
        filterOption={customFilterOption}
        placeholder={'Select a Shift Supervisor'}
        isOptionDisabled={(option) => {
          const data = option as Selectable<ShiftSupervisor>
          return fields.some((supervisor) => supervisor.id === data.value)
        }}
        onChange={(newValue) => {
          const value = newValue as Selectable<ShiftSupervisor>
          if (value.value?.toString() === '0') {
            return addSupervisor({
              id: Date.now(),
              requestType: SupervisorRequestType.FreeForm,
            })
          }

          addSupervisor({
            ...pick(['id', 'name', 'email', 'phoneNumber'], value),
            requestType: SupervisorRequestType.ExistingUser,
          })
        }}
      />
      {fields.map((field, index) => (
        <SupervisorCard
          key={field.key}
          supervisor={field}
          index={index}
          errors={errors.supervisors?.[index]}
          register={register}
          sendInvite={field.sendInvite ?? true}
          onRemove={() => removeSupervisor(index)}
        />
      ))}
      {supervisorsError ? (
        <Message mt={3} variant={'error'} systemLevel={false}>
          {supervisorsError.toString()}
        </Message>
      ) : null}
    </Container>
  )
}
